import { computed, useCookie, useDayjs, usePlayerAccountClient } from '#imports'
import type { UseQueryOptions } from '../../types'

export const useUserInfo = (options: Pick<UseQueryOptions<any>, 'enabled' | 'lazy' | 'server'> = {}) => {
    const dayjs = useDayjs()
    const { useGetUserInfo } = usePlayerAccountClient()

    const _loggedInBefore = useCookie<number | string | undefined>('loggedInBefore')
    const userJustLoggedIn = useCookie<true | undefined>('userJustLoggedIn', { secure: true })
    const userJustSignedUp = useCookie<true | undefined>('userJustSignedUp', { secure: true })
    const returnUrlAfterLogin = useCookie<string | undefined>('returnUrlAfterLogin', { secure: true, maxAge: dayjs.duration({ minutes: 30 }).asSeconds() })

    const query = useGetUserInfo(options)

    const isLoggedIn = computed(() => !!query.data.value)
    const isStaff = computed(() => query.data.value?.isStaff ?? false)
    /**
     * Is the user an existing user that has logged in before.
     *
     * It is determined by the `loggedInBefore` cookie and returns `true` if the cookie has any value.
     */
    const isExisting = computed(() => !!_loggedInBefore.value || isLoggedIn.value)

    const playerState = computed(() => {
        if (isLoggedIn.value) {
            return 'logged_in'
        }

        if (isExisting.value) {
            return 'existing'
        }

        return 'new'
    })

    return query.enrich({
        userJustLoggedIn,
        userJustSignedUp,
        returnUrlAfterLogin,
        isLoggedIn,
        isStaff,
        isExisting,
        playerState,
    })
}
